import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Megaphone, Plus, X } from 'lucide-react';
import { IconBadge } from '../components/Badges';
import { Button } from '@/components/ui/button';
import {
  deleteAnnouncement,
  listAnnouncements,
  postAnnouncement,
  type AnnouncementsResult,
} from '../announcements';

interface AnnouncementsCardProps {
  /** Admin+ can post and remove */
  canManage: boolean;
  isSuperAdmin: boolean;
}

/**
 * Pinned notes from the office at the top of the Dashboard. Hidden when
 * there's nothing to show and the viewer can't post. Until
 * sql/dashboard/001 runs, only a SuperAdmin sees a setup hint.
 */
export default function AnnouncementsCard({
  canManage,
  isSuperAdmin,
}: Readonly<AnnouncementsCardProps>) {
  const [result, setResult] = useState<AnnouncementsResult | null>(null);
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');
  const [expires, setExpires] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const reload = useCallback(() => {
    listAnnouncements().then(setResult);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!result) return null;
  if (result.status === 'missing') {
    return isSuperAdmin ? (
      <p className="mx-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600 sm:mx-8">
        Announcements are ready to use once
        sql/dashboard/001_dashboard_announcements.sql is run in the Supabase SQL
        editor.
      </p>
    ) : null;
  }
  if (result.status === 'error') return null;
  if (result.items.length === 0 && !canManage) return null;

  const submit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await postAnnouncement(body, expires || null);
      setBody('');
      setExpires('');
      setComposing(false);
      reload();
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remove this announcement for everyone?')) return;
    try {
      await deleteAnnouncement(id);
      reload();
    } catch (e) {
      alert(`Couldn't remove it: ${(e as Error).message}`);
    }
  };

  return (
    <section
      aria-label="Announcements"
      className="mx-4 rounded-xl border border-tifpa-rose bg-[color-mix(in_oklch,var(--tifpa-light-rose)_60%,white)] p-4 sm:mx-8"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-modern-charcoal">
          <IconBadge icon={Megaphone} size="sm" />
          Announcements
        </h2>
        {canManage && !composing && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 border-tifpa-rose bg-white text-xs"
            onClick={() => setComposing(true)}
          >
            <Plus className="mr-1 h-3 w-3" />
            Post
          </Button>
        )}
      </div>

      {result.items.length === 0 && !composing && (
        <p className="mt-2 text-sm text-modern-light-charcoal">
          Nothing posted. Use Post to share a note with the whole team.
        </p>
      )}

      <ul className="mt-2 space-y-2">
        {result.items.map(a => (
          <li
            key={a.id}
            className="flex items-start gap-2 rounded-lg bg-white/80 px-3 py-2"
          >
            <p className="min-w-0 flex-1 whitespace-pre-line text-sm text-slate-800">
              {a.body}
              <span className="mt-0.5 block text-xs text-slate-500">
                {a.authorName ?? 'Office'} ·{' '}
                {formatDistanceToNow(new Date(a.createdAt), {
                  addSuffix: true,
                })}
                {a.expiresAt &&
                  ` · until ${new Date(a.expiresAt).toLocaleDateString()}`}
              </span>
            </p>
            {canManage && (
              <button
                type="button"
                onClick={() => void remove(a.id)}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Remove announcement"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {composing && (
        <div className="mt-3 space-y-2 rounded-lg bg-white p-3">
          <label className="block text-xs font-medium text-slate-700">
            Message
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              maxLength={1000}
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-tifpa-rose"
              placeholder="Office closed Friday for the holiday."
            />
          </label>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs font-medium text-slate-700">
              Show until (optional)
              <input
                type="date"
                value={expires}
                onChange={e => setExpires(e.target.value)}
                className="mt-1 block rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <div className="ml-auto flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => {
                  setComposing(false);
                  setSaveError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={saving || !body.trim()}
                onClick={() => void submit()}
              >
                {saving ? 'Posting…' : 'Post'}
              </Button>
            </div>
          </div>
          {saveError && <p className="text-xs text-red-700">{saveError}</p>}
        </div>
      )}
    </section>
  );
}
